import type { AutocompleteProps } from '@mui/material';
import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import useId from '@mui/utils/useId';
import PropTypes from 'prop-types';
import type { HTMLAttributes, ReactNode } from 'react';

/** @param {typeof propTypes & import('@mui/material').AutocompleteProps} */
export const MuiAutocomplete = ({
    'data-testid': dataTestId,
    renderOption: userRenderOption,
    endAdornment,
    ...props
}: {
    'data-testid'?: string;
    renderOption?: AutocompleteProps['renderOption'];
    endAdornment?: ReactNode;
} & AutocompleteProps) => {
    const id = useId();
    const value = props.value
        ? props.options.find((option: any) => props.getOptionValue(option) === props.value)
        : null;

    const defaultRenderOption =
        dataTestId && !userRenderOption
            ? (liProps: HTMLAttributes<HTMLLIElement>, option: unknown) => {
                  const label =
                      props.getOptionLabel?.(option) ??
                      (option == null
                          ? ''
                          : typeof option === 'string' || typeof option === 'number'
                            ? String(option)
                            : typeof option === 'object' && 'label' in (option as object)
                              ? String((option as { label: unknown }).label)
                              : '');
                  return (
                      <li {...liProps} data-testid={`${dataTestId}-option`}>
                          {label}
                      </li>
                  );
              }
            : undefined;

    const renderOption = userRenderOption ?? defaultRenderOption;

    return (
        <Autocomplete
            id={id}
            options={props.options}
            getOptionKey={props.getOptionValue}
            renderInput={(params) => (
                <TextField
                    {...params}
                    inputProps={{
                        ...params.inputProps,
                        ...(dataTestId ? { 'data-testid': dataTestId } : {}),
                    }}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {props.loading ? <CircularProgress color='primary' size={24} /> : null}
                                {endAdornment}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                    label={props.label}
                    placeholder={props.placeholder}
                    error={props.error}
                    helperText={props.helperText}
                />
            )}
            {...props}
            {...(renderOption && { renderOption })}
            value={value || null}
        />
    );
};

const propTypes = {
    label: PropTypes.string,
    placeholder: PropTypes.string,
    getOptionValue: PropTypes.func.isRequired,
    loading: PropTypes.bool,
    error: PropTypes.bool,
    helperText: PropTypes.string,
};
MuiAutocomplete.propTypes = propTypes;
